import uid from "common/utils/uid";
import type { DocMigration } from "../docMigration";

function computeFloorThicknesses(name: "FRAMING" | "SUBFLOOR", thickness: number): number {
  switch (name) {
    case "FRAMING":
      if (thickness > 6.0) {
        return thickness - 0.75;
      } else if (thickness >= 2.0) {
        return thickness - 0.5;
      } else {
        return 0;
      }
    case "SUBFLOOR":
      if (thickness > 6.0) {
        return 0.75;
      } else if (thickness >= 2.0) {
        return 0.5;
      } else {
        return thickness;
      }
  }
  return 0.0;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
const migration_0_0_189: DocMigration = {
  maxSchemaVersion: "0.0.189",
  description: "Move floor settings to floor catalog",

  apply(json: any) {
    const { settings } = json;
    const assemblies = {};
    const components = {};

    const levelIdToFloorPlateThickness = {};

    for (const id in settings) {
      if (settings[id].type === "FLOOR_SETTINGS") {
        const setting = settings[id];
        const { parentId } = setting;

        const parent = json.levels[parentId] ?? json.settingsSets[parentId];
        const parentName: string = parent?.name;

        let { floorPlateThickness } = settings[id];
        floorPlateThickness = parseFloorplateThicknessFormula(floorPlateThickness);

        delete settings[id].floorPlateThickness;

        const levelId = json.levels[parentId] ? parentId : "default";
        levelIdToFloorPlateThickness[levelId] = floorPlateThickness;

        let idStructuralFraming = uid();
        const structuralThickness = computeFloorThicknesses("FRAMING", floorPlateThickness);

        const existingFraming = Object.keys(components).find(
          (id) => components[id].componentType === "STRUCTURE" && components[id].thickness === structuralThickness,
        );

        if (existingFraming) {
          idStructuralFraming = existingFraming;
        } else {
          const name = formatInchesFraction(structuralThickness);
          const structuralFraming = {
            componentType: "STRUCTURE",
            structuralType: "FRAMING",
            name: `${name} Structural Framing`,
            thickness: structuralThickness,
          };
          components[idStructuralFraming] = structuralFraming;
        }

        let idSubFlooring = uid();
        const subFloorThickness = computeFloorThicknesses("SUBFLOOR", floorPlateThickness);

        const existingSubFloor = Object.keys(components).find(
          (id) => components[id].componentType === "SUBFLOOR" && components[id].thickness === subFloorThickness,
        );

        if (existingSubFloor) {
          idSubFlooring = existingSubFloor;
        } else {
          const name = formatInchesFraction(subFloorThickness);
          const subFlooring = {
            componentType: "SUBFLOOR",
            name: `${name} Subfloor`,
            thickness: subFloorThickness,
          };
          components[idSubFlooring] = subFlooring;
        }

        const floorAssemblyId = uid();
        const floorAssembly = {
          name: `${parentName} Floor Assembly`,
          structureId: idStructuralFraming,
          subFloorId: idSubFlooring,
        };
        assemblies[floorAssemblyId] = floorAssembly;
        settings[id].floorAssembly = floorAssemblyId;
      }
    }

    if (json.rooms) {
      json.floors = json.floors ?? {};

      for (const roomId in json.rooms) {
        const room = json.rooms[roomId];
        if (room.floorElevationOffset) {
          let floorAssemblyId: string | undefined = undefined;

          if (room.floorElevationOffset < 0) {
            const { levelId } = json.blocks[room.blockId];
            const level = json.levels[levelId];
            const floorPlateThickness =
              levelIdToFloorPlateThickness[levelId] ?? levelIdToFloorPlateThickness["default"] ?? 12;

            const newFloorPlateThickness = floorPlateThickness + room.floorElevationOffset;

            let idStructuralFraming = uid();
            const structuralThickness = computeFloorThicknesses("FRAMING", newFloorPlateThickness);

            const existingFraming = Object.keys(components).find(
              (id) => components[id].componentType === "STRUCTURE" && components[id].thickness === structuralThickness,
            );

            if (existingFraming) {
              idStructuralFraming = existingFraming;
            } else {
              const name = formatInchesFraction(structuralThickness);
              const structuralFraming = {
                componentType: "STRUCTURE",
                structuralType: "FRAMING",
                name: `${name} Structural Framing`,
                thickness: structuralThickness,
              };
              components[idStructuralFraming] = structuralFraming;
            }

            let idSubFlooring = uid();
            const subFloorThickness = computeFloorThicknesses("SUBFLOOR", newFloorPlateThickness);

            const existingSubFloor = Object.keys(components).find(
              (id) => components[id].componentType === "SUBFLOOR" && components[id].thickness === subFloorThickness,
            );

            if (existingSubFloor) {
              idSubFlooring = existingSubFloor;
            } else {
              const name = formatInchesFraction(subFloorThickness);
              const subFlooring = {
                componentType: "SUBFLOOR",
                name: `${name} Subfloor`,
                thickness: subFloorThickness,
              };
              components[idSubFlooring] = subFlooring;
            }

            floorAssemblyId = uid();
            const floorAssembly = {
              name: `${level.name}/${room.name} Floor Assembly`,
              structureId: idStructuralFraming,
              subFloorId: idSubFlooring,
            };
            assemblies[floorAssemblyId] = floorAssembly;
          }

          const floorId = uid();
          const floor = {
            entityType: "FLOOR",
            assemblyType: "SOLID",
            id: floorId,
            name: "Solid Floor",
            parentId: room.id,
            topDatumOffset: room.floorElevationOffset,
            sortIndex: 0,
          };

          if (floorAssemblyId !== undefined) {
            (floor as any).assemblyId = floorAssemblyId;
          }

          delete room.floorElevationOffset;

          json.floors[floorId] = floor;
        }
      }
    }

    if (Object.keys(components).length) {
      const settingsSetId = uid();
      const settingsSet = {
        entityType: "SETTINGS_SET",
        name: "Floor Catalog Settings Set",
        id: settingsSetId,
        sortIndex: 0,
      };

      if (!json.settingsSets) {
        json.settingsSets = {};
      }

      json.settingsSets[settingsSetId] = settingsSet;

      const floorCatalogSettingsId = uid();
      const floorCatalogSettings = {
        id: floorCatalogSettingsId,
        type: "FLOOR_CATALOG_SETTINGS",
        entityType: "SETTINGS",
        name: "Floor Catalog Settings",
        sortIndex: 0,
        parentId: settingsSetId,
        floorAssemblies: assemblies,
        floorComponents: components,
      };
      json.settings[floorCatalogSettingsId] = floorCatalogSettings;
    }

    return json;
  },
};

function formatInchesFraction(inches: number, maxDenominator = 8, allowZeroFeet: boolean = false): string {
  const sign = inches < 0 ? "-" : "";
  inches = Math.abs(inches);
  const feetComponent = Math.floor(inches / 12);
  const inchComponent = Math.floor(inches) % 12;
  const decimalInch = inches - Math.floor(inches);

  // early exit for round inches
  if (decimalInch === 0) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent}"`;
    }

    return `${sign}${feetComponent}'-${inchComponent}"`;
  }

  const decimalInchRounded = Math.round(decimalInch * maxDenominator);
  const fractionalResult = toFraction(decimalInchRounded, maxDenominator);
  const additionalInches = fractionalResult.isFraction ? 0 : fractionalResult.resultNum ?? 0;
  const additionalFeet = inchComponent + additionalInches === 12 ? 1 : 0;

  // if fraction rounding bubbles up to feet.
  if (additionalFeet === 1) {
    return `${sign}${feetComponent + additionalFeet}'-0"`;
  }

  // if fraction rounding bubbles up to inches.
  if (decimalInchRounded === 0 || !fractionalResult.isFraction) {
    if (feetComponent === 0 && !allowZeroFeet) {
      return `${sign}${inchComponent + additionalInches}"`;
    }
    return `${sign}${feetComponent}'-${inchComponent + additionalInches}"`;
  }

  const fractionalInches = fractionalResult.isFraction ? fractionalResult.resultString : "0";

  const inchesAndFractions = inchComponent === 0 ? `${fractionalInches}"` : `${inchComponent} ${fractionalInches}"`;
  if (feetComponent === 0 && !allowZeroFeet) {
    return `${sign}${inchesAndFractions}`;
  }
  return `${sign}${feetComponent}'-${inchesAndFractions}`;
}

function toFraction(numerator: number, denominator: number) {
  const greatestCommonD = (a: number, b: number): number => {
    return b ? greatestCommonD(b, a % b) : a;
  };
  const greatestCommonDenom = greatestCommonD(numerator, denominator);

  const numVal = numerator / greatestCommonDenom;
  const denVal = denominator / greatestCommonDenom;

  if (numVal === 0) {
    return { isFraction: false, resultNum: 0 };
  }
  if (numVal === denVal) {
    return { isFraction: false, resultNum: 1 };
  }

  return { isFraction: true, resultString: `${numVal}/${denVal}` };
}

function parseFloorplateThicknessFormula(value: any) {
  if (value === undefined) {
    return value;
  }

  if (typeof value === "number") {
    return value;
  }

  if (typeof value === "object") {
    const formula = value.formula;
    const tryNumber = parseFloat(formula);
    if (!isNaN(tryNumber)) {
      return tryNumber;
    }
    return undefined;
  }

  return undefined;
}

export default migration_0_0_189;
